import { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { collection, query, where, getDocs, addDoc, deleteDoc, updateDoc, doc } from "firebase/firestore";

const ManageCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [updatedCampaignName, setUpdatedCampaignName] = useState("");
  const [updatedPostingInterval, setUpdatedPostingInterval] = useState("");
  const [listingsCount, setListingsCount] = useState({});

  // ✅ Fetch Campaigns & Listings Count
  useEffect(() => {
    if (!auth.currentUser) return;

    const fetchData = async () => {
      try {
        const campaignQuery = query(collection(db, "campaigns"), where("userId", "==", auth.currentUser.uid));
        const campaignSnapshot = await getDocs(campaignQuery);
        const campaignData = campaignSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const listingQuery = query(collection(db, "listings"), where("userId", "==", auth.currentUser.uid));
        const listingSnapshot = await getDocs(listingQuery);
        const listingData = listingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const listingsCountData = {};
        listingData.forEach(listing => {
          if (listing.campaignId) {
            listingsCountData[listing.campaignId] = (listingsCountData[listing.campaignId] || 0) + 1;
          }
        });

        setCampaigns(campaignData);
        setListingsCount(listingsCountData);
      } catch (error) {
        console.error("Error fetching campaigns and listings:", error);
      }
    };

    fetchData();
  }, []);

  // ✅ Create Campaign
  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) return alert("Please enter a campaign name.");

    try {
      const docRef = await addDoc(collection(db, "campaigns"), {
        name: newCampaignName.trim(),
        userId: auth.currentUser.uid,
        postingInterval: 12,
        lastPostedAt: null,
      });

      setCampaigns([...campaigns, { id: docRef.id, name: newCampaignName, postingInterval: 12, lastPostedAt: null }]);
      setNewCampaignName("");
    } catch (error) {
      console.error("Error creating campaign:", error);
      alert("Failed to create campaign.");
    }
  };

  // ✅ Delete Campaign
  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;

    try {
      await deleteDoc(doc(db, "campaigns", campaignId));
      setCampaigns(campaigns.filter((campaign) => campaign.id !== campaignId));
    } catch (error) {
      console.error("Error deleting campaign:", error);
      alert("Failed to delete campaign.");
    }
  };

  // ✅ Edit Campaign
  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setUpdatedCampaignName(campaign.name);
    setUpdatedPostingInterval(campaign.postingInterval);
  };

  // ✅ Save Edited Campaign
  const handleSaveEdit = async () => {
    if (!editingCampaign) return;
    if (!updatedCampaignName.trim() || !updatedPostingInterval) return alert("Please fill all fields.");

    try {
      const campaignRef = doc(db, "campaigns", editingCampaign.id);
      await updateDoc(campaignRef, {
        name: updatedCampaignName.trim(),
        postingInterval: parseInt(updatedPostingInterval, 10),
      });

      setCampaigns(
        campaigns.map((campaign) =>
          campaign.id === editingCampaign.id ? { ...campaign, name: updatedCampaignName, postingInterval: updatedPostingInterval } : campaign
        )
      );

      setEditingCampaign(null);
    } catch (error) {
      console.error("Error updating campaign:", error);
      alert("Failed to update campaign.");
    }
  };

  return (
    <div className="manage-campaigns-container">
      <h2>Manage Campaigns</h2>

      {/* ✅ Create Campaign Form */}
      <div className="campaign-form">
        <input 
          type="text" 
          placeholder="Campaign Name" 
          value={newCampaignName} 
          onChange={(e) => setNewCampaignName(e.target.value)} 
          required 
        />
        <button onClick={handleCreateCampaign}>Create Campaign</button>
      </div>

      {/* ✅ Campaigns Table - FULL WIDTH */}
      <table className="campaigns-table">
        <thead>
          <tr>
            <th>Campaign Name</th>
            <th>Posting Frequency (Hours)</th>
            <th>Total Listings</th>
            <th>Last Posted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.length > 0 ? (
            campaigns.map(campaign => (
              <tr key={campaign.id}>
                <td>{campaign.name}</td>
                <td>{campaign.postingInterval} hours</td>
                <td>{listingsCount[campaign.id] || 0}</td>
                <td>{campaign.lastPostedAt ? new Date(campaign.lastPostedAt.seconds * 1000).toLocaleString() : "Never"}</td>
                <td className="action-buttons">
                  <button className="edit-button" onClick={() => handleEditCampaign(campaign)}>✏️ Edit</button>
                  <button className="delete-button" onClick={() => handleDeleteCampaign(campaign.id)}>🗑️ Delete</button>
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="5">No campaigns found.</td></tr>
          )}
        </tbody>
      </table>

      {/* ✅ Edit Campaign Form */}
      {editingCampaign && (
        <div className="edit-form">
          <h3>Edit Campaign</h3>
          <div className="form-group">
            <label>Campaign Name</label>
            <input type="text" value={updatedCampaignName} onChange={(e) => setUpdatedCampaignName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Posting Frequency (Hours)</label>
            <input type="number" value={updatedPostingInterval} onChange={(e) => setUpdatedPostingInterval(e.target.value)} min="1" />
          </div>
          <div className="button-row">
            <button onClick={handleSaveEdit}>Save Changes</button>
            <button onClick={() => setEditingCampaign(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCampaigns;
